import React from 'react';

import { Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';

import { createReactRenderer } from 'root/apps/helpers/react-renderer';
import {
  LSPaymentHardwareSetupStatus,
  useLightspeedPaymentsSetupStatus,
} from 'root/apps/hooks/useLightspeedPaymentsSetupStatus';
import useLocalizedLink, { LS_PAY_LINKS } from 'root/apps/hooks/useLocalizedLink';

import {
  LinkSpacer,
  StyledBannerContentFlex,
  StyledBodyText,
  StyledButton,
  StyledCard,
  StyledImageBox,
  StyledLink,
  StyledTitle,
} from './Common.style';

const LS_PAY_CONFIG_ROUTE = '/settings/lightspeed-payments/configuration';

export const PaymentSetupBanner: React.FC = () => {
  const handleGoToPaymentProcessingClick = () => {
    window.PageRouter.goTo(LS_PAY_CONFIG_ROUTE);
  };

  const localizedPaymentsLink = useLocalizedLink(LS_PAY_LINKS.SETUP_LIGHTSPEED_PAYMENTS);
  const localizedHardwareLink = useLocalizedLink(LS_PAY_LINKS.SETUP_LIGHTSPEED_PAYMENTS_HARDWARE, {
    nl: 'en-us',
  });

  const { status } = useLightspeedPaymentsSetupStatus();

  const shouldDisplayBanner =
    status !== null && status !== LSPaymentHardwareSetupStatus.TRANSACTIONAL;

  if (!shouldDisplayBanner) {
    return null;
  }

  return (
    <StyledCard data-test="payments-setup-banner">
      <Flex flexDirection="row" height="100%">
        <StyledImageBox flexShrink={0}>
          <img
            src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/payments/lspayments-complete-setup-banner.png"
            alt="Lightspeed Payments"
            width="100%"
            height="100%"
            style={{ objectFit: 'contain' }}
          />
        </StyledImageBox>
        <StyledBannerContentFlex
          flexDirection="column"
          maxWidth="426px"
          maxHeight="366px"
          width="fit-content"
          justifyContent="space-between"
        >
          <StyledTitle>Complete your Payments setup</StyledTitle>
          <StyledBodyText>
            Activate your gateway(s) and pair your terminal(s) to start taking payments with
            Lightspeed.
          </StyledBodyText>
          <LinkSpacer flexDirection="column">
            <StyledLink
              href={localizedPaymentsLink}
              target="_blank"
              data-test="payments-setup-banner-link-1"
            >
              Setting up Lightspeed Payments{' '}
              <IconExternalLink color={theme.textStyles.link.color} />
            </StyledLink>
            <StyledLink
              href={localizedHardwareLink}
              target="_blank"
              data-test="payments-setup-banner-link-2"
            >
              Setting up your Lightspeed Payments hardware{' '}
              <IconExternalLink color={theme.textStyles.link.color} />
            </StyledLink>
          </LinkSpacer>
          <StyledButton
            variant="secondary"
            fill
            onClick={handleGoToPaymentProcessingClick}
            data-test="payments-setup-banner-button"
          >
            Go to Payment Processing
            <IconArrowRight />
          </StyledButton>
        </StyledBannerContentFlex>
      </Flex>
    </StyledCard>
  );
};

export default PaymentSetupBanner;

const RenderedComponent = createReactRenderer({
  component: PaymentSetupBanner,
  selector: '[data-react-root="PaymentSetupBanner"]',
});

history.onChangeState(
  [{ form_name: 'ui_tab', tab: 'financial_services' }],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
